import dayjs from 'dayjs';
import * as German from 'dayjs/locale/de';
import * as English from 'dayjs/locale/en';
import CustomParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getHolidays } from 'feiertagejs';

import { dayjsDate } from '../types/time';

dayjs?.extend(isBetween);
dayjs?.extend(CustomParseFormat);
dayjs?.extend(utc);
dayjs?.extend(timezone);

export type HandoverDates = Record<string, { delivery_fee: number }>;

const getDateLocale = (lang: string) => (lang === 'de' ? German : English);

const addSeconds = (date: dayjsDate, seconds: number) => {
  return dayjs(date).add(seconds, 'second').toDate();
};

export const isBusinessDay = (date: dayjsDate) => {
  const workingWeekdays = [1, 2, 3, 4, 5];
  if (workingWeekdays.includes(dayjs(date).day())) return true;

  return false;
};

export const isWeekend = (date: dayjsDate) => {
  return !isBusinessDay(date);
};

const toUTC = (date: Date): Date => {
  return dayjs(date).add(-date.getTimezoneOffset(), 'minute').toDate();
};

export const range = (
  fromDate: Date,
  toDate: Date,
  iterateFn: (date: Date, interval: number) => Date = addSeconds,
  interval: number = 1
): Array<Date> => {
  const result = [];
  let currentDate = fromDate;
  while (currentDate <= toDate) {
    result.push(currentDate);
    currentDate = iterateFn(currentDate, interval);
  }

  return result;
};

export const fromEuropeFormatToDate = (value: string): Date =>
  dayjs(value, 'DD.MM.YYYY').toDate();

export const reverseAbilipayDate = (date: string) =>
  dayjs(date, 'DD.MM.YYYY').toDate();

export const parseUTCDate = (date: string) =>
  dayjs(date, 'YYYY-MM-DD').toDate();

// Get UTC date and ignore the hours
export const getUTCDate = (date: dayjsDate) => {
  const format = 'YYYY-MM-DD';
  const formattedDate = dayjs(date).format(format);
  const finalDate = toUTC(dayjs(formattedDate, format).toDate());

  return finalDate.toUTCString();
};

// Get the Diff between 2 dates in ms
export const dateDiff = (firstDate: Date, secondDate: Date) => {
  // Day in ms
  const day = 1000 * 60 * 60 * 24;

  const firstDateMs = firstDate.getTime();
  const secondDateMs = secondDate.getTime();

  const diff = secondDateMs - firstDateMs;

  return Math.round(diff / day);
};

export const getAgeInYears = (birthday: Date) => dayjs().diff(birthday, 'year');

export const getCurrentMonthInShort = (lang: string = 'de') => {
  try {
    return dayjs().locale(getDateLocale(lang)).format('MMM');
  } catch (e) {
    console.error(e);

    return '';
  }
};

export const getCurrentYear = () => dayjs().year();

export const getDateMonthWeekends = (date: Date): Date[] => {
  const monthDays = dayjs(date).daysInMonth();

  const weekends = [];

  for (let i = 1; i <= monthDays; i++) {
    const dayInMonth = new Date(date.getFullYear(), date.getMonth(), i);

    if (isWeekend(dayInMonth)) {
      weekends.push(dayInMonth);
    }
  }

  return weekends;
};

export const formatDate = (
  date: dayjsDate,
  dateFormat: string,
  lang: string = 'de'
) => {
  try {
    return dayjs(date).locale(getDateLocale(lang)).format(dateFormat);
  } catch (e) {
    console.error(e);

    return '';
  }
};

export const getMinMaxAvailableDate = (
  carHandoverAvailability: HandoverDates
) => {
  let minDate = new Date();
  let maxDate = new Date();
  const availableDates = Object.keys(carHandoverAvailability).map((date) =>
    dayjs(date).valueOf()
  );
  if (availableDates.length) {
    const earliestAvailableDate = new Date(
      Math.min.apply(null, availableDates)
    );
    const latestAvailableDate = new Date(Math.max.apply(null, availableDates));

    minDate = earliestAvailableDate;
    maxDate = latestAvailableDate;
  }

  return { minDate, maxDate };
};

export const isWithinRange = (date: Date, start: Date, end: Date) => {
  return date >= start && date <= end;
};

export const addBusinessDays = (date: dayjsDate, number: number) => {
  const numericDirection = number < 0 ? -1 : 1;
  let currentDay = dayjs(date);
  let daysRemaining = Math.abs(number);

  while (daysRemaining > 0) {
    currentDay = currentDay.add(numericDirection, 'd');

    if (isBusinessDay(currentDay)) daysRemaining -= 1;
  }

  return currentDay.toDate();
};

export const toUniversalTime = (date: Date) =>
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );

export const isPublicHolidayDE = (date: dayjsDate) => {
  const formattedDate = dayjs(date).format('YYYY-MM-DD');
  const year = dayjs(formattedDate).get('year');
  const publicHolidaysDE = getHolidays(year, 'ALL');

  let isHoliday = false;
  publicHolidaysDE.forEach((day) => {
    if (day.dateString === formattedDate) {
      isHoliday = true;
    }
  });

  return isHoliday;
};

export const getClosestWorkingDay = (receivedDate: dayjsDate) => {
  let date = receivedDate;
  while (isPublicHolidayDE(date)) {
    date = dayjs(date).add(1, 'day');
  }

  return date;
};

// return min and max dates in an array of string dates
export const getMinMaxDates = (dates: string[]) => {
  const arrtime = dates.map((str) => new Date(str).getTime());
  const minDate = new Date(Math.min(...arrtime));
  const maxDate = new Date(Math.max(...arrtime));

  return { minDate, maxDate };
};

export const removeLocalTimezoneOffset = (date: Date): Date => {
  return new Date(
    `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
  );
};

// some hardcoded german text was used here, will need some refactoring to support other languages
export const getFormattedLowConfidenceDate = ({
  date,
  deviationWeek,
  locale = 'de',
  capitalize = false,
}: {
  date: string;
  deviationWeek: number;
  locale?: string;
  capitalize?: boolean;
}) => {
  if (deviationWeek > 0) {
    const startDate = dayjs(date);
    const endDate = startDate.add(deviationWeek, 'week');

    if (deviationWeek <= 2) {
      const formattedStartDate = dayjs(startDate)
        .locale(locale)
        .format('DD.MM.YYYY');
      const formattedEndDate = dayjs(endDate)
        .locale(locale)
        .format('DD.MM.YYYY');

      const betweenText = capitalize ? 'Zwischen' : 'zwischen';

      return `${betweenText} ${formattedStartDate} und ${formattedEndDate}`;
    } else {
      const formattedStartDate = dayjs(startDate).locale(locale).format('MMMM');
      const formattedStartDateWithYear = dayjs(startDate)
        .locale(locale)
        .format('MMMM YYYY');
      const formattedEndDate = dayjs(endDate)
        .locale(locale)
        .format('MMMM YYYY');

      const isSameYear = dayjs(startDate).isSame(endDate, 'year');
      const isSameMonth = dayjs(startDate).isSame(endDate, 'month');

      if (isSameYear && isSameMonth) {
        return `${formattedEndDate}`;
      }

      if (isSameYear) {
        return `${formattedStartDate}-${formattedEndDate}`;
      }

      return `${formattedStartDateWithYear}-${formattedEndDate}`;
    }
  }

  return dayjs(date).locale(locale).format('dddd, DD.MM.YYYY');
};
